import { Component, OnInit } from "@angular/core";
import { DomSanitizer, SafeHtml } from "@angular/platform-browser";
import { FormControl } from "@angular/forms";
import { MatDialog } from "@angular/material/dialog";
import { ActivatedRoute, Router } from "@angular/router";
import {
  DWSIntegration,
  DWSIntegrationConnector,
  WineryIntegration,
} from "@interfaces/dws/integration";
import { Login } from "@services/accounts.service";
import { WineryIntegrationsService } from "@services/dws/winery-integrations.service";
import { DWSLanguagesService } from "@services/dws";
import { access } from "@util/route/access";
import { tr } from "@util/tr";
import { toast } from "@web/util/toast";
import { forkJoin, of, Observable } from "rxjs";
import { observe } from "@web/util/loading/loading";
import { locale } from "@util/locale";
import { catchError, map } from "rxjs/operators";
import {
  WsSearchbarConfig,
  WsSearchResult,
} from "../../../util/ws-ui/ws-searchbar/ws-searchbar.component";
import { WsChipType } from "../../../util/ws-ui/ws-chip/ws-chip.component";
import { marker } from "@biesbjerg/ngx-translate-extract-marker";

@Component({
  selector: "web-integrations",
  templateUrl: "./integrations.component.html",
  styleUrls: ["./integrations.component.scss"],
})
export class IntegrationsComponent implements OnInit {
  categoriesControl = new FormControl();
  statusControl = new FormControl("all");
  searchTerm = new FormControl();
  categories: String[] = [];
  integrations: DWSIntegrationConnector[] = [];
  wIntegrations: WineryIntegration[] = [];
  isNewForDays = 90;

  statusControlViews = [
    { id: "all", name: tr("All") },
    { id: "true", name: tr("Installed") },
    { id: "false", name: tr("Not installed") },
  ];
  categoriesViews: { id: string; name: string }[] = [];

  integrationSearchConfig: WsSearchbarConfig = {
    placeholder: tr("Search"),
    tooltip: tr("Search by name"),
    debounceTime: 300,
    minSearchLength: 9999,
    showIcon: true,
    showSpinner: false,
    variant: "default",
    expandDirection: "right",
  };

  login: Login;
  lang!: string;
  readonly WsChipType = WsChipType;

  constructor(
    private dialog: MatDialog,
    private route: ActivatedRoute,
    private wineryIntegrationsService: WineryIntegrationsService,
    private router: Router,
    private dwsLanguagesService: DWSLanguagesService,
    private sanitizer: DomSanitizer
  ) {
    this.login = access(route).data("login");
    this.lang = this.dwsLanguagesService.getCurrentLanguageIso();
  }

  searchIntegrations = (
    searchText: string
  ): Observable<DWSIntegrationConnector[]> => {
    const filtered = this.integrations.filter((integration) => {
      const translatedName = this.getTranslatedName(integration.integration);
      return (
        translatedName.toLowerCase().includes(searchText.toLowerCase()) ||
        integration.integrationName
          .toLowerCase()
          .includes(searchText.toLowerCase())
      );
    });
    return of(filtered).pipe(
      catchError((error) => {
        console.error("Integration search error:", error);
        return of([]);
      })
    );
  };

  getTranslatedName(item: any): string {
    if (item.nameEn) {
      return tr(item.nameEn);
    } else if (item.nameIt) {
      return tr(item.nameIt);
    } else if (item.name) {
      return tr(item.name);
    }
    return item.integrationName || item.displayName || "";
  }

  getIntegrationDisplayName(integration: any): string {
    return (
      this.getTranslatedName(integration) ||
      integration.name ||
      integration.integrationName ||
      ""
    );
  }

  getIntegrationDescription(integration: any): string {
    let translations = integration.disclaimerTranslations;
    if (typeof translations === "string") {
      try {
        translations = JSON.parse(translations.trim());
      } catch {
        translations = undefined;
      }
    }

    if (!translations || typeof translations !== "object") {
      return integration.description || integration.disclaimer || "";
    }

    if (translations[this.lang]) {
      return translations[this.lang];
    }

    if (translations["en"]) {
      return translations["en"];
    }

    if (translations["it"]) {
      return translations["it"];
    }

    const availableKeys = Object.keys(translations);
    if (availableKeys.length > 0) {
      return translations[availableKeys[0]];
    }

    return integration.description || integration.disclaimer || "";
  }

  /**
   * CMS/API sometimes stores markup with angle brackets escaped (&lt;p …&gt;)
   * or double-escaped (&amp;lt;…). Decode so [innerHTML] receives real HTML.
   * Stops before re-processing strings that already contain raw tags only.
   */
  private integrationDescriptionFromEncodedText(s: string): string {
    if (!s) {
      return s;
    }
    if (!/&lt;|&amp;(?:lt|gt|amp|quot|#\d+|#x[0-9a-f]+);/i.test(s)) {
      return s;
    }
    const textarea = document.createElement("textarea");
    let current = s;
    for (let i = 0; i < 4; i++) {
      textarea.innerHTML = current;
      const next = textarea.value;
      if (next === current) {
        break;
      }
      current = next;
      if (!/&lt;|&amp;(?:lt|gt);/i.test(current)) {
        break;
      }
    }
    return current;
  }

  getIntegrationDescriptionHtml(integration: any): SafeHtml {
    const raw = this.getIntegrationDescription(integration) || "";
    return this.sanitizer.bypassSecurityTrustHtml(
      this.integrationDescriptionFromEncodedText(raw)
    );
  }

  // Result mapper for displaying search results
  mapIntegrationForDisplay = (
    integration: DWSIntegrationConnector
  ): WsSearchResult => {
    const integrationName =
      this.getTranslatedName(integration.integration) ||
      integration.integrationName;
    const category = integration.integrationType || "NO CAT";
    const isInstalled = this.isInstalledInThisWinery(integration.integration!);
    const isEnabled = this.isEnabledInThisWinery(integration.integration!);

    let status = "";
    if (this.isWhatsAppIntegration(integration.integration!, integration)) {
      status =
        integration.integration!.active !== false
          ? tr("Connected")
          : tr("Unavailable");
    } else if (isInstalled && isEnabled) {
      status = tr("Installed");
    } else if (isInstalled && !isEnabled) {
      status = tr("Disconnected");
    } else {
      status = tr("Not installed");
    }

    return {
      id: integration.integration?.id!,
      displayData: {
        name: integrationName,
        category: tr("integrationTypes." + category) || category,
        status: status,
      },
    };
  };

  ngOnInit() {
    this.loadPage();
  }

  loadPage() {
    observe(
      forkJoin([
        this.wineryIntegrationsService.getIntegrations(),
        this.wineryIntegrationsService
          .getWineryIntegrations(this.login.company.id)
          .pipe(
            catchError((err) => {
              console.error("Error fetching winery integrations:", err);
              // return an empty array or a default value
              return of([]);
            })
          ),
      ])
    ).subscribe({
      next: ([integrations, wineryIntegrations]) => {
        var integrationsWithRequiredLevel = this.filterLogicForPermissions(integrations);
        this.integrations = integrationsWithRequiredLevel;
        this.prepareCategoriesAndIntegrations(integrationsWithRequiredLevel);
        this.wIntegrations = wineryIntegrations || [];
      },
      error: (err) => {
        console.error("Error fetching integrations:", err);
        toast(tr("An error occurred. Try later"));
      },
    });
  }

  get isSuperadmin(): boolean {
    return !!this.login?.authorization?.is_admin;
  }

  isStripeIntegration(integration: DWSIntegration | undefined): boolean {
    return integration?.code === "stripe";
  }

  filterLogicForPermissions(integrations: DWSIntegrationConnector[]) {
    return integrations.filter(
      (integration) => {
        if (
          this.isStripeIntegration(integration.integration) &&
          !this.isSuperadmin
        ) {
          return false;
        }
        return (
          this.isWhatsAppIntegration(integration.integration, integration) ||
          integration.integration?.requiredLevel! > 0 ||
          false
        );
      }
    );
  }

  prepareCategoriesAndIntegrations(integrations: DWSIntegrationConnector[]) {
    const categories = integrations
      .map((integration) => integration.integrationType || "NO CAT")
      .filter((value, index, self) => self.indexOf(value) === index);
    
    const categoriesOrdered = [
      "EXTERNAL_CALENDAR",
      "PAYMENTS",
      "OTA",
      "ECOMMERCE",
      "ERP",
      "SHIPPING_PARTNER",
      "CASH_REGISTER",
      "COMMUNICATIONS",
      "WIFI",
      "FISCALITY",
    ];
    
    const sortedCategories = categories.sort(
      (a: string, b: string) =>
        categoriesOrdered.indexOf(a) - categoriesOrdered.indexOf(b)
    );
    
    this.categories = sortedCategories;
    this.categoriesViews = sortedCategories.map((category) => {
      return {
        id: category,
        name: tr("integrationTypes." + category) || category,
      };
    });
    this.integrations = integrations;
  }

  get filteredCategories() {
    let categoriesOrdered = [
      "EXTERNAL_CALENDAR",
      "PAYMENTS",
      "OTA",
      "ECOMMERCE",
      "ERP",
      "SHIPPING_PARTNER",
      "CASH_REGISTER",
      "COMMUNICATIONS",
      "WIFI",
      "FISCALITY",
    ];
    return (
      this.categoriesControl.value?.length > 0
        ? this.categoriesControl.value || this.categories
        : this.categories
    )
      ?.filter((c: string) => this.filteredIntegrations(c)?.length > 0)
      .sort(
        (a: string, b: string) =>
          categoriesOrdered.indexOf(a) - categoriesOrdered.indexOf(b)
      );
  }

  filteredIntegrations(category: string) {
    let integrations = this.integrations
      .filter(
        (integration) =>
          (integration.integrationType === category ||
            !!!integration.integrationType ||
            !!!category) &&
          (!this.searchTerm.value ||
            integration.integrationName
              .toLowerCase()
              .includes(this.searchTerm.value.toLowerCase()) ||
            this.getTranslatedName(integration.integration)
              .toLowerCase()
              .includes(this.searchTerm.value.toLowerCase())) &&
          (this.statusControl.value === "all" ||
            (this.statusControl.value === "true") ===
              this.isEnabledInThisWinery(integration.integration!))
      )
      .sort((a, b) => a.integrationName.localeCompare(b.integrationName));

    const stripeIntegrationIndex = integrations.findIndex(
      (integration) => integration.integration?.code === "stripe"
    );
    if (stripeIntegrationIndex > -1) {
      const [stripeIntegration] = integrations.splice(
        stripeIntegrationIndex,
        1
      );
      integrations.unshift(stripeIntegration);
    }

    return integrations;
  }

  isWhatsAppIntegration(
    integration: DWSIntegration | undefined,
    connector?: DWSIntegrationConnector
  ): boolean {
    const code = integration?.code?.toLowerCase();
    const name = integration?.name?.toLowerCase();
    const integrationName = connector?.integrationName?.toLowerCase();
    return (
      code === "whatsapp" ||
      name === "whatsapp" ||
      integrationName === "whatsapp"
    );
  }

  generateBadge(
    integration: DWSIntegration,
    connector?: DWSIntegrationConnector
  ): [string | undefined, string | undefined] {
    if (
      this.isWhatsAppIntegration(integration, connector) &&
      integration.active !== false
    ) {
      return [tr("Connected"), "#68CC45"];
    }

    if (this.isInstalledInThisWinery(integration)) {
      if (
        this.isEnabledInThisWinery(integration) &&
        this.isConnected(integration)
      ) {
        return [tr("Installed"), "#68CC45"];
      } else {
        return [tr("Disconnected"), "#eb4646"];
      }
    }

    if (!integration.active) {
      if (new Date(integration?.createdAt!) > new Date()) {
        return [tr("Next"), "#F5BA58"];
      } else {
        return [tr("Unavailable"), "#eb4646"];
      }
    } else {
      const futureDate = new Date();
      futureDate.setDate(futureDate.getDate() - this.isNewForDays);

      if (new Date(integration?.createdAt!) > futureDate) {
        return [tr("New"), "#47A7FF"];
      }
    }

    return [undefined, undefined];
  }

  getBadgeColor(
    integration: DWSIntegration,
    connector?: DWSIntegrationConnector
  ): string | undefined {
    return this.generateBadge(integration, connector)[1];
  }

  getBadgeText(
    integration: DWSIntegration,
    connector?: DWSIntegrationConnector
  ): string | undefined {
    return this.generateBadge(integration, connector)[0];
  }

  toggleActiveState(integration: DWSIntegration, active: boolean) {
    this.updateIntegrationOrOutputError(
      {
        wineryId: this.login.company.id,
        integrationId: integration.id,
        integration: integration,
      } as WineryIntegration,
      active
    );
  }

  isConnected(integration: DWSIntegration): boolean {
    var founded = this.wIntegrations.find(
      (wInt) =>
        wInt && wInt.integration && wInt.integration?.id === integration!.id
    );

    return founded?.connected || false;
  }

  isInstalledInThisWinery(integration: DWSIntegration): boolean {
    var founded = this.wIntegrations.find(
      (wInt) =>
        wInt && wInt.integration && wInt.integration?.id === integration!.id
    );
    return !!founded;
  }

  isEnabledInThisWinery(integration: DWSIntegration): boolean {
    var founded = this.wIntegrations.find(
      (wInt) =>
        wInt &&
        wInt.integration &&
        wInt.integration?.id === integration!.id &&
        wInt.disabled === false
    );
    return !!founded;
  }

  updateIntegrationOrOutputError(
    integration: WineryIntegration,
    active: boolean
  ) {
    if (active) {
      observe(
        this.wineryIntegrationsService.connectIntegration(
          this.login.company.id,
          integration
        )
      ).subscribe({
        next: () => {
          toast(tr("Integration saved successfully"));
        },
        error: (err) => {
          console.error("Error updating integration:", err);
          toast(tr("An error occurred. Try later"));
          this.loadPage();
        },
        complete: () => {
          this.loadPage();
        },
      });
    } else {
      observe(
        this.wineryIntegrationsService.disconnectIntegration(
          this.login.company.id,
          integration
        )
      ).subscribe({
        next: () => {
          toast(tr("Integration saved successfully"));
        },
        error: (err) => {
          console.error("Error updating integration:", err);
          toast(tr("An error occurred. Try later"));
          this.loadPage();
        },
        complete: () => {
          this.loadPage();
        },
      });
    }
  }

  goToIntegration(integrationId: string) {
    this.router.navigate(["integrations", integrationId]);
    document.querySelector(".content-wrapper-ws")?.scrollIntoView();
  }

  isRequiredLevelGreaterThanTwo(integration: DWSIntegration): boolean {
    return (integration?.requiredLevel ?? 0) > 2 || false;
  }

  isDetailsButtonDisabled(integration: DWSIntegration): boolean {
    return this.isRequiredLevelGreaterThanTwo(integration) && !this.isInstalledInThisWinery(integration);
  }

  getDetailsButtonTooltip(integration: DWSIntegration): string {
    if (this.isDetailsButtonDisabled(integration)) {
      return tr("Request to Sales");
    }
    return "";
  }

  optionsStatusChanged(
    event: { id: string; name: string; selected: boolean }[]
  ) {
    for (const e of event) {
      if (e.selected) {
        this.statusControl.setValue(e.id);
      }
    }
  }

  optionsCategories(event: { id: string; name: string; selected: boolean }[]) {
    for (var e of event) {
      if (e.selected) {
        const currentValues = this.categoriesControl.value || [];
        if (!currentValues.includes(e.id)) {
          this.categoriesControl.setValue([...currentValues, e.id]);
        }
      } else {
        const currentValues = this.categoriesControl.value || [];
        this.categoriesControl.setValue(
          currentValues.filter((id: string) => id !== e.id)
        );
      }
    }
  }

  onSearchSubmit(searchText: string) {
    this.searchTerm.setValue(searchText);
  }

  onSearchTextChanged(searchText: string) {
    this.searchTerm.setValue(searchText);
  }

  onIntegrationSelected(event: {
    item: DWSIntegrationConnector;
    mappedResult: WsSearchResult;
  }) {
    this.goToIntegration(event.mappedResult.id);
  }
}
